import {
  Box,
  Button,
  Divider,
  lighten,
  Stack,
  Typography,
} from "@mui/material";
import Header from "../Headers/Header";
import Product from "../Product";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import { useIsMobile } from "src/hooks/useIsMobile";
import Subheader from "../Headers/Subheader";
import { Check } from "@mui/icons-material";
import { theme } from "src/assets/theme";
import { useTranslation } from "react-i18next";
import { usePrices } from "src/context/PricesProvider";
import { useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useProduct } from "src/context/ProductProvider";
import { Coupon, ProductPrice } from "src/api/types";
import PaymentProcessor from "../PaymentProcessor";
import TermsOfConditions from "../TermsOfConditions";
import ApplyCoupon from "../ApplyCoupon";
import { useGetPatientCouponsQuery } from "src/api/services/patientCouponsApi";
import CouponView from "../CouponView";
import ProductsDisplay from "src/components/ProductsDisplay";
import { productInformationArray } from "src/constants/producttInformation";

const FemaleHairLossOTPaymentInformation = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const products = usePrices();
  const [searchParams, setSearchParams] = useSearchParams();
  const form = searchParams.get("form")?.toLowerCase();
  const filteredProducts = useMemo(() => {
    if (products == null || products.length === 0) return [];

    const productForm = form === "dropper" ? "serum" : form;

    return products.filter((p) =>
      p.name.toLowerCase().startsWith(productForm || ""),
    );
  }, [form, products]);
  const [selectedProduct, setSelectedProduct] = useState(
    searchParams.get("price") != null
      ? filteredProducts.find((p) => p.stripe_id === searchParams.get("price"))
      : filteredProducts.find((product) => product.default_selection),
  );
  const productSlug = useProduct((p) => p.slug);
  const billingModel = useProduct((p) => p.billing_model);
  const isGlp1 = useProduct((p) => p.is_glp1);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const page = "payment-information";
  const { data: coupons = [] } = useGetPatientCouponsQuery({
    product_slug: productSlug,
    redeemed: false,
  });

  const informationArray = productInformationArray["fhl"];

  const handleProductChange = useCallback((product: ProductPrice) => {
    setSelectedProduct(product);
  }, []);

  const strikedPrice = useMemo(() => {
    if (selectedProduct == null) {
      return null;
    }

    return selectedProduct.competitor_price;
  }, [selectedProduct]);

  const defaultProductPrice = useMemo(() => {
    if (selectedProduct == null) {
      return "0";
    }

    const appliedDiscount = coupon?.amount ?? 0;
    const discount = coupons.reduce(
      (acc, c) => (acc += c.amount) - appliedDiscount,
      0,
    );

    const price = Math.max(
      selectedProduct?.amount - discount,
      selectedProduct.amount_limit ?? 0,
    );

    return price.toLocaleString();
  }, [coupon?.amount, coupons, selectedProduct]);

  const saving = useMemo(() => {
    if (selectedProduct == null) return;
    const defaultPrice = Number(defaultProductPrice.replace(",", ""));

    return (strikedPrice || 0) - defaultPrice;
  }, [defaultProductPrice, selectedProduct, strikedPrice]);

  useEffect(() => {
    if (selectedProduct?.stripe_id != null) {
      searchParams.set("price", selectedProduct?.stripe_id || "");
      setSearchParams(searchParams);
    }
  }, [searchParams, selectedProduct?.stripe_id, setSearchParams]);

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Stack gap={3}>
          <Header />
          <Product />
        </Stack>
        <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
          <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
            <Subheader />
          </Typography>
          {informationArray.map((i) => (
            <Box key={i} display="flex" gap={1} alignItems="center">
              <Check
                sx={{ color: theme.palette.primary.main }}
                fontSize="medium"
              />
              <Typography variant={isMobile ? "body2" : "body1"}>
                {t(i)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
      <Stack gap={3}>
        <ProductsDisplay
          page={page}
          handleProductChange={handleProductChange}
          products={filteredProducts}
          selectedProduct={selectedProduct}
          form={form}
        />
      </Stack>
      <Stack gap={4}>
        <Stack gap={2}>
          <ApplyCoupon setCoupon={setCoupon} coupon={coupon} />
          {coupons.map((c) => (
            <CouponView coupon={c} />
          ))}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontSize={"18px"}>
              {t(`${page}.freeShipping`)}
            </Typography>
            <Typography fontSize={"18px"}>{t(`${page}.free`)}</Typography>
          </Stack>
          <Divider />
          <Stack gap={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography fontSize={"22px"} fontWeight={600}>
                {t(`${page}.total`)}
              </Typography>
              <Stack direction={"row"} gap={1} alignItems={"center"}>
                {strikedPrice ? (
                  <Typography
                    fontSize={"22px"}
                    sx={{ textDecoration: "line-through" }}
                    variant="subtitle1"
                  >
                    ${strikedPrice}
                  </Typography>
                ) : null}
                <Typography fontSize={"22px"} fontWeight={600}>
                  ${defaultProductPrice}
                </Typography>
              </Stack>
            </Stack>
            <Typography
              color={theme.palette.primary.main}
              fontStyle="italic"
              fontWeight={600}
              fontSize={"18px"}
            >
              {t(`${page}.${billingModel}.disclaimer`)}
            </Typography>
          </Stack>
          <Button
            variant="text"
            sx={{
              backgroundColor: lighten(theme.palette.secondary.main, 0.3),
              boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
              color: "#ffffff",
              fontSize: "16px",
              fontWeight: 700,
              borderRadius: "10px",
              textAlign: "center",
            }}
            onClick={() => handleFocusIntoView("payment-input")}
          >
            {t(
              `${page}.totalSavings.${productSlug}-${form}.${selectedProduct?.number_of_months}`,
              {
                saving,
              },
            )}
          </Button>
          <Divider sx={{ position: "relative", top: 15 }} />
        </Stack>
        {selectedProduct ? (
          <PaymentProcessor
            product={selectedProduct}
            coupon={coupon}
            dueToday={Number(defaultProductPrice)}
          />
        ) : null}
      </Stack>
      <TermsOfConditions
        page={page}
        productSlug={productSlug}
        isGlp1={isGlp1}
        numberOfMonths={selectedProduct ? selectedProduct.number_of_months : 1}
      />
    </Stack>
  );
};

export default FemaleHairLossOTPaymentInformation;
